import { enMessages } from "./en";
import { jaMessages, type AppMessages } from "./ja";

export type SupportedLocale = "ja" | "en";

const LOCALE_MESSAGES: Readonly<Record<SupportedLocale, AppMessages>> = {
  ja: jaMessages,
  en: enMessages,
};

let currentLocale: SupportedLocale = "ja";

export function detectBrowserLocale(): SupportedLocale {
  const langs =
    navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];
  for (const lang of langs) {
    if (lang.toLowerCase().startsWith("ja")) {
      return "ja";
    }
  }
  return "en";
}

export function isSupportedLocale(value: string): value is SupportedLocale {
  return value === "ja" || value === "en";
}

export function getCurrentLocale(): SupportedLocale {
  return currentLocale;
}

export function setCurrentLocale(locale: SupportedLocale): void {
  currentLocale = locale;
}

export function getMessages(): AppMessages {
  return LOCALE_MESSAGES[currentLocale];
}

export function formatInteger(value: number): string {
  return new Intl.NumberFormat(currentLocale, {
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatDecimal(value: number, digits: number): string {
  return new Intl.NumberFormat(currentLocale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

export function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat(currentLocale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatDuration(durationMs: number): string {
  const messages = getMessages();
  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return messages.offline.durationHoursMinutes(
      formatInteger(hours),
      formatInteger(minutes),
    );
  }
  if (minutes > 0) {
    return messages.offline.durationMinutesSeconds(
      formatInteger(minutes),
      formatInteger(seconds),
    );
  }
  return messages.offline.durationSeconds(formatInteger(seconds));
}
